import { FC, useEffect, useMemo, useState } from 'react';

import { getSeasonConfig } from './get-season-config';
import { useSeasonalTheme } from './seasonal-theme-context';
import { SeasonalTokenOverrides } from './types';

const isDarkTime = (hour: number) => hour < 6 || hour >= 19;

const SAVED_COLOR_MODE_KEY = 'bonusly:seasonal-theme:saved-color-mode';

/**
 * Written with the current resolved color mode ('light' | 'dark') by
 * setSeasonalColorMode and removed by restoreUserColorMode. Read by the
 * Rails iframe sync script to stay in sync with the seasonal mode.
 * Also written synchronously in toggleTheme (before reload) so it is
 * present even before this component's effects have a chance to fire.
 */
const ACTIVE_MODE_KEY = 'bonusly:seasonal-theme:active-mode';

/**
 * Written with the season's colorModeStrategy ('time-based' | 'force-dark')
 * so the Rails iframe sync script can re-derive the correct mode from the
 * current time instead of relying on a potentially stale ACTIVE_MODE_KEY.
 * Also written synchronously in toggleTheme before reload.
 */
const STRATEGY_KEY = 'bonusly:seasonal-theme:color-mode-strategy';

/**
 * Save the user's current color mode before the seasonal theme takes over.
 * Only saves if we haven't already saved (prevents overwriting the original
 * preference if the component re-renders).
 */
const saveUserColorMode = () => {
	if (localStorage.getItem(SAVED_COLOR_MODE_KEY)) return;
	const chakraMode = localStorage.getItem('chakra-ui-color-mode') || 'light';
	const bonuslyPref = localStorage.getItem('bonusly-color-mode-preference') || 'light';
	localStorage.setItem(SAVED_COLOR_MODE_KEY, JSON.stringify({ chakraMode, bonuslyPref }));
};

/**
 * Set the Chakra localStorage keys to the seasonal mode so Chakra initializes
 * with the correct value on page load — no fighting needed.
 */
const setSeasonalColorMode = (mode: 'light' | 'dark') => {
	localStorage.setItem('chakra-ui-color-mode', mode);
	localStorage.setItem('bonusly-color-mode-preference', mode);
	localStorage.setItem(ACTIVE_MODE_KEY, mode);
};

/**
 * Restore the user's color mode from before the seasonal theme was enabled.
 * Writes back to all Chakra localStorage keys and updates DOM attributes.
 */
const restoreUserColorMode = () => {
	const raw = localStorage.getItem(SAVED_COLOR_MODE_KEY);
	localStorage.removeItem(SAVED_COLOR_MODE_KEY);
	localStorage.removeItem(ACTIVE_MODE_KEY);
	localStorage.removeItem(STRATEGY_KEY);

	let chakraMode = 'light';
	let bonuslyPref = 'light';

	if (raw) {
		try {
			const parsed = JSON.parse(raw);
			chakraMode = parsed.chakraMode || 'light';
			bonuslyPref = parsed.bonuslyPref || 'light';
		} catch {
			// Fallback to light if corrupted
		}
	}

	localStorage.setItem('chakra-ui-color-mode', chakraMode);
	localStorage.setItem('bonusly-color-mode-preference', bonuslyPref);
	document.documentElement.dataset.theme = chakraMode;
	document.body.classList.remove('chakra-ui-light', 'chakra-ui-dark');
	document.body.classList.add(`chakra-ui-${chakraMode}`);
};

/**
 * Generate CSS that sets --chakra-colors-* variables from resolved token values.
 */
const generateSeasonalCSS = (overrides: SeasonalTokenOverrides, useDark: boolean, themeCss?: string): string => {
	const pick = (tv: { default: string; _dark: string }) => (useDark ? tv._dark : tv.default);
	const { primary, bg, text, border } = overrides.colors;

	return `
		:root {
			--chakra-colors-primary-fg: ${pick(primary.fg)} !important;
			--chakra-colors-primary-fgStrong: ${pick(primary.fgStrong)} !important;
			--chakra-colors-primary-fgStronger: ${pick(primary.fgStronger)} !important;
			--chakra-colors-primary-fgStrongest: ${pick(primary.fgStrongest)} !important;
			--chakra-colors-primary-bg: ${pick(primary.bg)} !important;
			--chakra-colors-primary-bgHover: ${pick(primary.bgHover)} !important;
			--chakra-colors-primary-bgActive: ${pick(primary.bgActive)} !important;
			--chakra-colors-primary-bgAlpha: ${pick(primary.bgAlpha)} !important;
			--chakra-colors-primary-bgAlphaHover: ${pick(primary.bgAlphaHover)} !important;
			--chakra-colors-primary-bgAlphaActive: ${pick(primary.bgAlphaActive)} !important;
			--chakra-colors-bg-canvas: ${pick(bg.canvas)} !important;
			--chakra-colors-bg-surface: ${pick(bg.surface)} !important;
			--chakra-colors-bg-soft: ${pick(bg.soft)} !important;
			--chakra-colors-bg-subtle: ${pick(bg.subtle)} !important;
			--chakra-colors-bg-topNav: ${pick(bg.topNav)} !important;
			--chakra-colors-bg-inset: ${pick(bg.inset)} !important;
			--chakra-colors-text-interactive: ${pick(text.interactive)} !important;
			--chakra-colors-border-default: ${pick(border.default)} !important;
			--chakra-colors-border-soft: ${pick(border.soft)} !important;
		}

		/* Meeting page: add padding and border to header and editor so they
		   don't run edge-to-edge against the gradient background */
		.spring-theme [data-component="meeting-header"] {
			padding: var(--chakra-space-3);
			border: 1px solid var(--chakra-colors-border-default);
		}
		.spring-theme [data-component="one-on-one-view"] :has(> .shared-editor-container) {
			border-radius: var(--chakra-radii-lg);
			overflow: hidden;
		}
		.spring-theme [data-component="one-on-one-view"] .shared-editor-container {
			padding: var(--chakra-space-3);
			border: 1px solid var(--chakra-colors-border-default);
			border-radius: var(--chakra-radii-lg);
		}
		.spring-theme [data-component="one-on-one-view"] [data-component="completed-meeting-view"] {
			padding: var(--chakra-space-3);
			border: 1px solid var(--chakra-colors-border-default);
		}

		/* The Bizy side panel brings its own opaque background (BIZY-669) — no
		   frosted override here, or the landscape bleeds through the panel. */

		/* Meeting context drawer (fob + expanded panel) and the header dropdown
		   panel (History / Info): frosted surface so content stays readable over
		   the landscape. bg-canvas is transparent in spring dark mode. */
		.spring-theme [data-component="meeting-context-drawer-fob"],
		.spring-theme [data-component="meeting-context-drawer-panel"],
		.spring-theme [data-component="meeting-header-dropdown-panel"] {
			background: var(--chakra-colors-whiteAlpha-900) !important;
			backdrop-filter: blur(44px);
			-webkit-backdrop-filter: blur(44px);
		}
		html[data-theme="dark"].spring-theme [data-component="meeting-context-drawer-fob"],
		html[data-theme="dark"].spring-theme [data-component="meeting-context-drawer-panel"],
		html[data-theme="dark"].spring-theme [data-component="meeting-header-dropdown-panel"] {
			background: var(--chakra-colors-blackAlpha-900) !important;
		}

		/* Scroll-fade gradients inside the frosted panels use bg-canvas as the
		   opaque end of the gradient. In spring dark mode bg-canvas is transparent,
		   so the fade disappears. Override to match the frosted panel surface. */
		.spring-theme [data-component="context-drawer-scroll-fade"] {
			background: linear-gradient(to bottom, var(--chakra-colors-whiteAlpha-900) 0%, transparent 100%) !important;
		}
		html[data-theme="dark"].spring-theme [data-component="context-drawer-scroll-fade"] {
			background: linear-gradient(to bottom, var(--chakra-colors-blackAlpha-900) 0%, transparent 100%) !important;
		}

		${themeCss ?? ''}
	`;
};

/**
 * Seasonal Theme Overrides
 *
 * When the seasonal theme is active:
 * 1. Saves the user's current color mode to a dedicated localStorage key
 * 2. Forces Chakra's DOM attributes (data-theme, body class) to the seasonal
 *    mode (light/dark based on time of day), using a MutationObserver to
 *    prevent other systems from overriding it
 * 3. Injects CSS variable overrides for the seasonal accent colors
 *
 * When disabled or feature flag removed:
 * 1. Restores the user's saved color mode from the dedicated key
 * 2. Removes the CSS overrides
 */
export const SeasonalThemeOverrides: FC = () => {
	const { isSeasonalThemeEnabled } = useSeasonalTheme();
	const seasonConfig = getSeasonConfig();
	const [hour, setHour] = useState(() => new Date().getHours());

	const strategy = seasonConfig.colorModeStrategy ?? 'force-dark';
	const useDark = !isSeasonalThemeEnabled ? false : strategy === 'force-dark' || isDarkTime(hour);

	useEffect(() => {
		const id = window.setInterval(() => setHour(new Date().getHours()), 60_000);
		return () => clearInterval(id);
	}, []);

	// Save user's color mode when the seasonal theme is confirmed active (flag + pref)
	useEffect(() => {
		if (isSeasonalThemeEnabled) {
			saveUserColorMode();
		}
	}, [isSeasonalThemeEnabled]);

	// Enforce the seasonal color mode on DOM and localStorage.
	// Gated by isSeasonalThemeEnabled (not localStorage) so the MutationObserver
	// stops immediately when the feature flag is removed (kill switch).
	useEffect(() => {
		if (!isSeasonalThemeEnabled) {
			if (localStorage.getItem(SAVED_COLOR_MODE_KEY)) {
				restoreUserColorMode();
			}
			return;
		}

		const root = document.documentElement;
		const body = document.body;
		const mode = useDark ? 'dark' : 'light';
		const oppositeMode = useDark ? 'light' : 'dark';

		setSeasonalColorMode(mode);
		try {
			localStorage.setItem(STRATEGY_KEY, strategy);
		} catch {
			/* localStorage unavailable — best effort */
		}

		const applyMode = () => {
			root.dataset.theme = mode;
			root.classList.add(seasonConfig.themeClassName);
			body.classList.remove(`chakra-ui-${oppositeMode}`);
			body.classList.add(`chakra-ui-${mode}`);
		};

		applyMode();

		const observer = new MutationObserver(() => {
			if (root.dataset.theme !== mode || !body.classList.contains(`chakra-ui-${mode}`)) {
				applyMode();
			}
		});

		observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
		observer.observe(body, { attributes: true, attributeFilter: ['class'] });

		return () => {
			observer.disconnect();
			root.classList.remove(seasonConfig.themeClassName);
		};
	}, [isSeasonalThemeEnabled, useDark, seasonConfig.themeClassName]);

	const buttonText = useMemo(() => {
		const randomIndex = Math.floor(Math.random() * seasonConfig.buttonTexts.length);
		return seasonConfig.buttonTexts[randomIndex];
	}, [seasonConfig.buttonTexts]);

	// Replace "Give recognition" button text with seasonal text
	useEffect(() => {
		if (!isSeasonalThemeEnabled) return;

		let foundButton = false;

		const replaceButtonText = () => {
			const giveBoxActions = document.querySelector('[data-testid="give-box-actions"]');
			if (!giveBoxActions) return false;

			const buttons = giveBoxActions.querySelectorAll('button');
			let replacedAny = false;

			buttons.forEach((button) => {
				const walker = document.createTreeWalker(button, NodeFilter.SHOW_TEXT, null);

				let node;
				while ((node = walker.nextNode())) {
					if (node.textContent && node.textContent.trim()) {
						const text = node.textContent.trim();
						if (text.includes('Give recognition') || text.includes('Give Recognition') || text === 'Give recognition') {
							node.textContent = node.textContent.replace(/Give recognition/gi, buttonText);
							replacedAny = true;
						}
					}
				}
			});

			return replacedAny;
		};

		if (replaceButtonText()) {
			foundButton = true;
		}

		let intervalId: number | undefined;
		if (!foundButton) {
			intervalId = window.setInterval(() => {
				if (replaceButtonText()) {
					foundButton = true;
					if (intervalId) {
						clearInterval(intervalId);
					}
				}
			}, 200);

			setTimeout(() => {
				if (intervalId) {
					clearInterval(intervalId);
				}
			}, 10000);
		}

		const observer = new MutationObserver(() => {
			replaceButtonText();
		});

		observer.observe(document.body, {
			childList: true,
			subtree: true,
		});

		return () => {
			if (intervalId) {
				clearInterval(intervalId);
			}
			observer.disconnect();
		};
	}, [isSeasonalThemeEnabled, buttonText]);

	if (!isSeasonalThemeEnabled) return null;

	return <style>{generateSeasonalCSS(seasonConfig.semanticTokenOverrides, useDark, seasonConfig.themeCss)}</style>;
};
